import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Calendar, Clock, User, Mail, Phone, MessageSquare, 
  CheckCircle, XCircle, Clock3, RefreshCw, UserCheck,
  AlertCircle, Edit, Trash2, Filter, Stethoscope, Users,
  CalendarCheck, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Consultations = () => {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all'); // 'all', 'bookings', 'appointments'
  const [editingBooking, setEditingBooking] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    status: '',
    confirmed_date: '',
    confirmed_time: '',
    notes: '',
    assigned_to: ''
  });

  const loadBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/consultations?include_appointments=true`;
      if (filterStatus !== 'all') {
        url += `&status=${filterStatus}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load consultation bookings');
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/consultations/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    loadBookings();
    loadStats();
  }, [filterStatus]);

  const openEditModal = (booking) => {
    setEditingBooking(booking);
    setEditForm({
      status: booking.status,
      confirmed_date: booking.confirmed_date || '',
      confirmed_time: booking.confirmed_time || '',
      notes: booking.notes || '',
      assigned_to: booking.assigned_to || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateBooking = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      
      // If it's an appointment, update via appointments API
      if (editingBooking.source === 'appointment') {
        const updateData = {
          status: editForm.status,
          notes: editForm.notes
        };
        
        // If date/time changed, update appointment_date
        if (editForm.confirmed_date && editForm.confirmed_time) {
          const newDateTime = new Date(`${editForm.confirmed_date}T${editForm.confirmed_time}`);
          updateData.appointment_date = newDateTime.toISOString();
        }
        
        await axios.put(
          `${API_URL}/api/appointments/${editingBooking.appointment_id}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Traditional consultation booking
        await axios.patch(
          `${API_URL}/api/consultations/${editingBooking.id}`,
          editForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success('Consultation updated successfully!');
      setShowEditModal(false);
      setEditingBooking(null);
      loadBookings();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteBooking = async (booking) => {
    const confirmMsg = booking.source === 'appointment' 
      ? 'Cancel this consultation appointment?' 
      : 'Delete this consultation booking?';
    
    if (!window.confirm(confirmMsg)) return;

    try {
      const token = localStorage.getItem('token');
      
      if (booking.source === 'appointment') {
        // Cancel appointment
        await axios.post(
          `${API_URL}/api/appointments/${booking.appointment_id}/cancel`,
          { reason: 'Cancelled from consultation dashboard' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Appointment cancelled');
      } else {
        // Delete booking
        await axios.delete(`${API_URL}/api/consultations/${booking.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Booking deleted');
      }
      
      loadBookings();
      loadStats();
    } catch (error) {
      toast.error('Failed to delete/cancel');
    }
  };

  const handleQuickAction = async (booking, action) => {
    try {
      const token = localStorage.getItem('token');
      
      if (booking.source === 'appointment') {
        // Use appointments API
        switch (action) {
          case 'confirm':
            await axios.post(
              `${API_URL}/api/appointments/${booking.appointment_id}/confirm`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Appointment confirmed');
            break;
          case 'complete':
            await axios.post(
              `${API_URL}/api/appointments/${booking.appointment_id}/complete`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Appointment completed');
            break;
          default:
            break;
        }
      } else {
        // Use consultations API
        await axios.patch(
          `${API_URL}/api/consultations/${booking.id}`,
          { status: action === 'confirm' ? 'confirmed' : 'completed' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Booking ${action}ed`);
      }
      
      loadBookings();
      loadStats();
    } catch (error) {
      toast.error(`Failed to ${action}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock3, label: 'Pending' },
      scheduled: { color: 'bg-blue-100 text-blue-700', icon: Calendar, label: 'Scheduled' },
      confirmed: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Confirmed' },
      completed: { color: 'bg-gray-100 text-gray-700', icon: CheckCircle, label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Cancelled' },
      rescheduled: { color: 'bg-orange-100 text-orange-700', icon: RefreshCw, label: 'Rescheduled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} border-0`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getSourceBadge = (booking) => {
    if (booking.source === 'appointment') {
      return (
        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
          <CalendarCheck className="h-3 w-3 mr-1" />
          Member Appointment
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
        <Users className="h-3 w-3 mr-1" />
        Public Booking
      </Badge>
    );
  };

  // Filter bookings by source
  const filteredBookings = bookings.filter(b => {
    if (filterSource === 'all') return true;
    if (filterSource === 'appointments') return b.source === 'appointment';
    if (filterSource === 'bookings') return b.source !== 'appointment';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Consultations</h2>
          <p className="text-gray-600">Manage consultation requests and member appointments</p>
        </div>
        <Button onClick={() => { loadBookings(); loadStats(); }} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-600">Total</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                <p className="text-xs text-gray-600">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
                <p className="text-xs text-gray-600">Confirmed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.completed}</p>
                <p className="text-xs text-gray-600">Completed</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-teal-50 to-blue-50">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-teal-600">{stats.appointments?.total || 0}</p>
                <p className="text-xs text-gray-600">From Appointments</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="space-y-3">
        {/* Status Filter */}
        <div className="flex gap-2 border-b">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 font-medium transition-colors capitalize ${
                filterStatus === status
                  ? 'text-teal-600 border-b-2 border-teal-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
        
        {/* Source Filter */}
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant={filterSource === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterSource('all')}
          >
            All Sources
          </Button>
          <Button 
            size="sm" 
            variant={filterSource === 'appointments' ? 'default' : 'outline'}
            onClick={() => setFilterSource('appointments')}
            className={filterSource === 'appointments' ? 'bg-blue-600' : ''}
          >
            <CalendarCheck className="h-4 w-4 mr-1" />
            Member Appointments
          </Button>
          <Button 
            size="sm" 
            variant={filterSource === 'bookings' ? 'default' : 'outline'}
            onClick={() => setFilterSource('bookings')}
            className={filterSource === 'bookings' ? 'bg-purple-600' : ''}
          >
            <Users className="h-4 w-4 mr-1" />
            Public Bookings
          </Button>
        </div>
      </div>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Stethoscope className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No consultations found</p>
            <p className="text-sm mt-1">Confirmed consultation appointments will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((booking) => {
            const canConfirm = ['pending', 'scheduled'].includes(booking.status);
            const canComplete = ['confirmed', 'scheduled'].includes(booking.status);
            const canCancel = !['cancelled', 'completed', 'rescheduled'].includes(booking.status);
            
            return (
              <Card key={booking.id} className={booking.source === 'appointment' ? 'border-l-4 border-l-blue-500' : 'border-l-4 border-l-purple-500'}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {booking.consultation_type}
                        </h3>
                        {getStatusBadge(booking.status)}
                        {getSourceBadge(booking)}
                        {booking.is_new_patient && (
                          <Badge variant="outline" className="text-xs">
                            New Patient
                          </Badge>
                        )}
                        {booking.is_member && (
                          <Badge variant="outline" className="text-xs bg-teal-50 text-teal-700">
                            <UserCheck className="h-3 w-3 mr-1" />
                            Member
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {booking.source === 'appointment' ? 'Scheduled' : 'Requested'} on {new Date(booking.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {canConfirm && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-600 hover:bg-green-50"
                          onClick={() => handleQuickAction(booking, 'confirm')}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Confirm
                        </Button>
                      )}
                      {canComplete && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 border-blue-600 hover:bg-blue-50"
                          onClick={() => handleQuickAction(booking, 'complete')}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Complete
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditModal(booking)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      {canCancel && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteBooking(booking)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{booking.name}</span>
                        {booking.patient_id && (
                          <Badge variant="outline" className="text-xs">{booking.patient_id}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span>{booking.email}</span>
                      </div>
                      {booking.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <span>{booking.phone}</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      {booking.preferred_date && (
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span>
                            {booking.source === 'appointment' ? 'Scheduled: ' : 'Preferred: '}
                            {new Date(booking.preferred_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {booking.preferred_time && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span>{booking.preferred_time}</span>
                        </div>
                      )}
                      {booking.confirmed_date && booking.status !== 'pending' && (
                        <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
                          <CheckCircle className="h-4 w-4" />
                          <span>Confirmed: {booking.confirmed_date} at {booking.confirmed_time}</span>
                        </div>
                      )}
                      {booking.assigned_to && (
                        <div className="flex items-center gap-2 text-sm">
                          <Stethoscope className="h-4 w-4 text-gray-400" />
                          <span>Provider: {booking.assigned_to}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {booking.message && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1">
                            {booking.source === 'appointment' ? 'Reason for Visit:' : 'Patient Message:'}
                          </p>
                          <p className="text-sm text-gray-600">{booking.message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {booking.notes && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-blue-700 mb-1">Internal Notes:</p>
                          <p className="text-sm text-blue-600">{booking.notes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Update Consultation</CardTitle>
              <CardDescription>
                Update status and details for {editingBooking.name}&apos;s {editingBooking.consultation_type}
                {editingBooking.source === 'appointment' && (
                  <Badge className="ml-2 bg-blue-100 text-blue-700">Member Appointment</Badge>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateBooking} className="space-y-4">
                <div className="space-y-2">
                  <Label>Status *</Label>
                  <select
                    required
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Confirmed Date</Label>
                    <Input
                      type="date"
                      value={editForm.confirmed_date}
                      onChange={(e) => setEditForm({ ...editForm, confirmed_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirmed Time</Label>
                    <Input
                      type="time"
                      value={editForm.confirmed_time}
                      onChange={(e) => setEditForm({ ...editForm, confirmed_time: e.target.value })}
                    />
                  </div>
                </div>

                {editingBooking.source !== 'appointment' && (
                  <div className="space-y-2">
                    <Label>Assigned To (Doctor/Staff)</Label>
                    <Input
                      value={editForm.assigned_to}
                      onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })}
                      placeholder="Enter staff member name"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Internal Notes</Label>
                  <Textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    placeholder="Add internal notes about this consultation..."
                    rows={4}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                  >
                    Update Consultation
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingBooking(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Consultations;
